import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listFowebProductBrands } from "../api/fowebproductBrands";
import { listFowebProductCats } from "../api/fowebproductCats";
import {
  createFowebPortalProduct,
  deleteFowebPortalProduct,
  deleteFowebPortalProductMedia,
  listFowebPortalProducts,
  updateFowebPortalProduct,
  uploadFowebPortalProductMedia,
} from "../api/fowebportalProducts";
import {
  createFowebPortalWatch,
  deleteFowebPortalWatch,
  deleteFowebPortalWatchMedia,
  listFowebPortalWatches,
  updateFowebPortalWatch,
  uploadFowebPortalWatchMedia,
} from "../api/fowebportalWatches";
import { getFowebPortalUser } from "../api/fowebportalUsers";
import LanguageToggle from "../components/LanguageToggle";
import BrandSearchSelect from "../components/BrandSearchSelect";
import PortalMediaUpload, { pendingMediaKey } from "../components/PortalMediaUpload";
import { useLanguage } from "../context/LanguageContext";
import { useToast } from "../context/ToastContext";
import { clearPortalSession, readPortalSession, savePortalSession } from "../utils/portalSession";
import {
  WATCH_BRANDS,
  PORTAL_FORM_SPECIFICATION_FIELDS,
  emptyWatchSpecification,
  watchSpecificationFrom,
} from "../utils/watchBrands";
import "./HomePage.css";
import PortalStatsCards from "./PortalStatsCards";

const PAGE_SIZE = 6;
const GENDER_TYPES = ["Men", "Women", "Kids", "Unisex", "Boys", "Girls"];

const EMPTY_WATCH_FORM = {
  watchName: "",
  cnName: "",
  category: "",
  sellingCostRMB: "",
  description: "",
  cnDescription: "",
  remarks: "",
  supplier: "",
  specification: emptyWatchSpecification(),
  primaryImage: "",
};

const EMPTY_FORM = {
  name: "",
  cnName: "",
  brand: "",
  category: "",
  subcategory: "",
  subsubcategory: "",
  genderType: "",
  sellingCostRMB: "",
  cnDescription: "",
  enDescription: "",
  remarks: "",
  supplier: "",
  primaryImage: "",
  secondaryImage: "",
};

function resolveImageRole(value, pendingFiles, addedPaths) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (!raw.startsWith("pending:")) return raw;
  const idx = (pendingFiles || []).findIndex((file) => pendingMediaKey(file) === raw);
  if (idx < 0) return "";
  return String(addedPaths?.[idx] || "").trim();
}

function idOf(ref) {
  if (!ref) return "";
  if (typeof ref === "string") return ref;
  return String(ref._id || "");
}

function brandLabel(brand) {
  if (!brand) return "—";
  if (typeof brand === "string") return brand;
  return brand.brand || "—";
}

function subName(item) {
  if (!item) return "";
  if (typeof item === "string") return item;
  return item.name || "";
}

function catalogNode(item) {
  if (typeof item === "string") {
    const name = String(item || "").trim();
    return name ? { name, cnName: "" } : null;
  }
  const name = String(item?.name || "").trim();
  if (!name) return null;
  return { name, cnName: String(item?.cnName || "").trim() };
}

function suggestProductName(next, brands, categories) {
  const brandRow = brands.find((item) => String(item._id) === String(next.brand));
  const brandName = String(brandRow?.brand || "").trim();
  const categoryRow = categories.find((item) => String(item._id) === String(next.category));
  const categoryName = String(categoryRow?.category || "").trim();
  const last =
    String(next.subsubcategory || "").trim() ||
    String(next.subcategory || "").trim() ||
    categoryName;
  return [brandName, last].filter(Boolean).join(" ");
}

function catalogLabel(node, lang) {
  if (!node) return "";
  if (typeof node === "string") return node;
  const name = String(node.name || node.category || "").trim();
  if (lang === "cn") {
    const cn = String(node.cnName || "").trim();
    if (cn) return cn;
  }
  return name;
}

function subcategoryOptionsOf(cat) {
  return (cat?.subcategory || []).map(catalogNode).filter(Boolean);
}

function subsubcategoryOptionsOf(cat, subcategory) {
  const match = (cat?.subcategory || []).find((item) => subName(item) === subcategory);
  const list = Array.isArray(match?.subsubcategory) ? match.subsubcategory : [];
  return list.map(catalogNode).filter(Boolean);
}

function productTitle(item, lang) {
  const en = String(item?.watchName || item?.name || "").trim();
  if (lang === "cn") return String(item?.cnName || en).trim() || "—";
  return en || "—";
}

function watchRow(doc) {
  return {
    ...doc,
    source: "watch",
    name: doc.watchName || "",
    brand: doc.category || "",
  };
}

function mediaSrc(path) {
  const raw = String(path || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return raw.startsWith("/") ? raw : `/${raw}`;
}

function firstImage(media) {
  const items = Array.isArray(media) ? media : [];
  return items.find((item) => !/\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(String(item || ""))) || "";
}

function statusOf(item) {
  return String(item?.statusObject?.status || "created");
}

function statusLabel(status, t) {
  if (status === "rejected") return t.statusRejected;
  if (status === "review") return t.statusReview;
  return t.statusCreated;
}

function formatRmb(value) {
  if (value === undefined || value === null || value === "") return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  const formatted = Number.isInteger(num)
    ? num.toLocaleString("en-SG")
    : num.toLocaleString("en-SG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `RMB ${formatted}`;
}

function formatSgDateParts(iso) {
  if (!iso) return { date: "—", time: "" };
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Singapore",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
  const get = (type) => parts.find((part) => part.type === type)?.value || "";
  return { date: `${get("year")}-${get("month")}-${get("day")}`, time: `${get("hour")}:${get("minute")}` };
}

function showingLabel(lang, from, to, total) {
  if (!total) return lang === "cn" ? "显示 0，共 0 条" : "Showing 0 of 0";
  if (lang === "cn") return `显示 ${from} - ${to}，共 ${total} 条`;
  return `Showing ${from} - ${to} of ${total}`;
}

function IconUser() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <circle cx="12" cy="8" r="3.2" />
      <path d="M5 19.2c.8-3.2 3.4-5 7-5s6.2 1.8 7 5" />
    </svg>
  );
}

function IconLogout() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M10 7V5a2 2 0 012-2h7v18h-7a2 2 0 01-2-2v-2" />
      <path d="M15 12H4m0 0l3-3m-3 3l3 3" />
    </svg>
  );
}

function IconPlus() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg className="h-4 w-4 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l5 5" />
    </svg>
  );
}

function IconEdit() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 20h4l10.5-10.5a2 2 0 00-2.8-2.8L5.2 17.2 4 20z" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M5 7h14M9 7V5h6v2M8 7l1 12h6l1-12" />
    </svg>
  );
}

function HomePage() {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const toast = useToast();
  const session = readPortalSession();
  const author = String(session?.name || "").trim();
  const [countUser, setCountUser] = useState(session);

  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [formKind, setFormKind] = useState(null);
  const [choosingType, setChoosingType] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [watchForm, setWatchForm] = useState(EMPTY_WATCH_FORM);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const hello = author
    ? lang === "cn"
      ? `${t.welcome}，${author}`
      : `${t.welcome}, ${author}`
    : t.welcome;

  const selectedCategory = useMemo(
    () => categories.find((row) => String(row._id) === String(form.category)) || null,
    [categories, form.category]
  );
  const subcategoryOptions = subcategoryOptionsOf(selectedCategory);
  const subsubcategoryOptions = subsubcategoryOptionsOf(selectedCategory, form.subcategory);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      if (statusFilter && statusOf(item) !== statusFilter) return false;
      if (!q) return true;
      const name = String(item.name || "").toLowerCase();
      const cnName = String(item.cnName || "").toLowerCase();
      return name.includes(q) || cnName.includes(q);
    });
  }, [items, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const from = filtered.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const to = Math.min(currentPage * PAGE_SIZE, filtered.length);
  const editingItem = items.find((row) => String(row._id) === String(editing)) || null;
  const editingStatus = statusOf(editingItem);

  const loadCounts = useCallback(async () => {
    const current = readPortalSession();
    if (!current?._id) return;
    try {
      const user = await getFowebPortalUser(current._id);
      savePortalSession(user);
      setCountUser(user);
    } catch {
      setCountUser(current);
    }
  }, []);

  const loadProducts = useCallback(async ({ silent = false } = {}) => {
    if (!author) return;
    try {
      if (!silent) setLoading(true);
      setError("");
      const [docs, watches] = await Promise.all([
        listFowebPortalProducts(author),
        listFowebPortalWatches(author),
      ]);
      const rows = [
        ...(Array.isArray(docs) ? docs : []).map((row) => ({ ...row, source: "foweb" })),
        ...(Array.isArray(watches) ? watches : []).map(watchRow),
      ].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
      setItems(rows);
    } catch (err) {
      setError(err?.message || "Could not load products.");
      if (!silent) setItems([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [author]);

  useEffect(() => {
    loadProducts();
    loadCounts();
    listFowebProductBrands()
      .then((docs) => setBrands(Array.isArray(docs) ? docs : []))
      .catch(() => setBrands([]));
    listFowebProductCats()
      .then((docs) => setCategories(Array.isArray(docs) ? docs : []))
      .catch(() => setCategories([]));
  }, [loadProducts, loadCounts]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  function onLogout() {
    clearPortalSession();
    navigate("/login", { replace: true });
  }

  function clearFilters() {
    setSearch("");
    setStatusFilter("");
  }

  function openCreate() {
    setChoosingType(false);
    setFormKind("product");
    setEditing("new");
    setForm(EMPTY_FORM);
    setFiles([]);
    setFormError("");
  }

  function openTypeChoice() {
    setChoosingType(true);
  }

  function closeTypeChoice() {
    setChoosingType(false);
  }

  function chooseWatch() {
    setChoosingType(false);
    setFormKind("watch");
    setEditing("new");
    setWatchForm(EMPTY_WATCH_FORM);
    setFiles([]);
    setFormError("");
  }

  function openEdit(item) {
    setEditing(String(item._id));
    if (item.source === "watch") {
      setFormKind("watch");
      const category = item.category || "";
      const specification = watchSpecificationFrom(item);
      if (category) specification.brand = category;
      setWatchForm({
        watchName: item.watchName || "",
        cnName: item.cnName || "",
        category,
        sellingCostRMB: item.sellingCostRMB ?? "",
        description: item.description || "",
        cnDescription: item.cnDescription || "",
        remarks: item.remarks || "",
        supplier: item.supplier || "",
        specification,
        primaryImage: item.primaryImage || "",
      });
      setFiles([]);
      setFormError("");
      return;
    }
    setFormKind("product");
    setForm({
      name: item.name || "",
      cnName: item.cnName || "",
      brand: idOf(item.brand),
      category: idOf(item.category),
      subcategory: item.subcategory || "",
      subsubcategory: item.subsubcategory || "",
      genderType: item.genderType || "",
      sellingCostRMB: item.sellingCostRMB ?? "",
      cnDescription: item.cnDescription || "",
      enDescription: item.enDescription || "",
      remarks: item.remarks || "",
      supplier: item.supplier || "",
      primaryImage: item.primaryImage || "",
      secondaryImage: item.secondaryImage || "",
    });
    setFiles([]);
    setFormError("");
  }

  function closeForm() {
    setEditing(null);
    setFormKind(null);
    setForm(EMPTY_FORM);
    setWatchForm(EMPTY_WATCH_FORM);
    setFiles([]);
    setFormError("");
  }

  function applyProductFields(patch) {
    setForm((current) => {
      const next = { ...current, ...patch };
      next.name = suggestProductName(next, brands, categories);
      return next;
    });
  }

  function onCategoryChange(value) {
    applyProductFields({
      category: value,
      subcategory: "",
      subsubcategory: "",
    });
  }

  function onSubcategoryChange(value) {
    applyProductFields({
      subcategory: value,
      subsubcategory: "",
    });
  }

  async function onSave(event) {
    event.preventDefault();
    const name = String(form.name || "").trim();
    const cnName = String(form.cnName || "").trim();
    if (!name && !cnName) {
      setFormError(t.nameRequired);
      toast.error(t.nameRequired);
      return;
    }
    if (!form.brand || !form.category || !form.genderType) {
      setFormError(t.formRequired);
      toast.error(t.formRequired);
      return;
    }
    const baseBody = {
      name,
      cnName,
      brand: form.brand,
      category: form.category,
      subcategory: form.subcategory,
      subsubcategory: form.subsubcategory,
      genderType: form.genderType,
      sellingCostRMB: form.sellingCostRMB === "" ? null : Number(form.sellingCostRMB),
      cnDescription: String(form.cnDescription || "").trim(),
      enDescription: String(form.enDescription || "").trim(),
      remarks: String(form.remarks || "").trim(),
      supplier: String(form.supplier || "").trim(),
      author,
    };
    try {
      setSaving(true);
      setFormError("");
      const wasNew = editing === "new";
      const pendingSnapshot = Array.from(files || []);
      let saved;
      let added = [];
      let mediaUploaded = false;
      let rolesFailed = false;

      if (wasNew) {
        saved = await createFowebPortalProduct({
          ...baseBody,
          primaryImage: "",
          secondaryImage: "",
        });
      } else {
        // Upload first when pending roles need final paths, then one update with roles.
        if (pendingSnapshot.length) {
          try {
            const uploadResult = await uploadFowebPortalProductMedia(editing, pendingSnapshot);
            added = Array.isArray(uploadResult?.added) ? uploadResult.added : [];
            mediaUploaded = true;
            if (uploadResult?.product) saved = uploadResult.product;
          } catch (uploadErr) {
            const message = uploadErr?.message || t.mediaUploadFailed;
            setFormError(message);
            toast.error(message);
            return;
          }
        }
        const primaryImage = resolveImageRole(form.primaryImage, pendingSnapshot, added);
        const secondaryImage = resolveImageRole(form.secondaryImage, pendingSnapshot, added);
        try {
          saved = await updateFowebPortalProduct(editing, {
            ...baseBody,
            primaryImage,
            secondaryImage,
          });
        } catch (updateErr) {
          const message = updateErr?.message || t.saveFailed;
          if (mediaUploaded) {
            setFormError(`${t.mediaRolesFailed} (${message})`);
            toast.error(t.mediaRolesFailed);
            loadProducts({ silent: true });
            return;
          }
          throw updateErr;
        }
      }

      if (wasNew) {
        const savedId = saved?._id;
        if (pendingSnapshot.length && savedId) {
          try {
            const uploadResult = await uploadFowebPortalProductMedia(savedId, pendingSnapshot);
            added = Array.isArray(uploadResult?.added) ? uploadResult.added : [];
            mediaUploaded = true;
            if (uploadResult?.product) saved = uploadResult.product;
          } catch (uploadErr) {
            toast.error(uploadErr?.message || t.mediaUploadFailed);
            closeForm();
            loadProducts({ silent: true });
            loadCounts();
            return;
          }
        }
        const primaryImage = resolveImageRole(form.primaryImage, pendingSnapshot, added);
        const secondaryImage = resolveImageRole(form.secondaryImage, pendingSnapshot, added);
        if ((primaryImage || secondaryImage) && savedId) {
          try {
            saved = await updateFowebPortalProduct(savedId, {
              ...baseBody,
              primaryImage,
              secondaryImage,
            });
          } catch (_roleErr) {
            rolesFailed = true;
          }
        }
      }

      closeForm();
      if (rolesFailed) toast.error(t.mediaRolesFailed);
      else toast.success(wasNew ? t.toastCreated : t.toastUpdated);
      loadProducts({ silent: true });
      loadCounts();
    } catch (err) {
      const message = err?.message || t.saveFailed;
      setFormError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  async function onSaveWatch(event) {
    event.preventDefault();
    const category = String(watchForm.category || "").trim();
    if (!category) {
      setFormError(t.watchBrandRequired);
      toast.error(t.watchBrandRequired);
      return;
    }
    const baseBody = {
      watchName: String(watchForm.watchName || "").trim(),
      cnName: String(watchForm.cnName || "").trim(),
      category,
      sellingCostRMB: watchForm.sellingCostRMB === "" ? null : Number(watchForm.sellingCostRMB),
      description: String(watchForm.description || "").trim(),
      cnDescription: String(watchForm.cnDescription || "").trim(),
      remarks: String(watchForm.remarks || "").trim(),
      supplier: String(watchForm.supplier || "").trim(),
      specification: watchSpecificationFrom({ specification: watchForm.specification }),
      author,
    };
    try {
      setSaving(true);
      setFormError("");
      const wasNew = editing === "new";
      const pendingSnapshot = Array.from(files || []);
      let saved;
      let added = [];
      let mediaUploaded = false;
      let rolesFailed = false;

      if (wasNew) {
        saved = await createFowebPortalWatch({ ...baseBody, primaryImage: "" });
        const savedId = saved?._id;
        if (pendingSnapshot.length && savedId) {
          try {
            const uploadResult = await uploadFowebPortalWatchMedia(savedId, pendingSnapshot);
            added = Array.isArray(uploadResult?.added) ? uploadResult.added : [];
            mediaUploaded = true;
            if (uploadResult) saved = uploadResult;
          } catch (uploadErr) {
            toast.error(uploadErr?.message || t.mediaUploadFailed);
            closeForm();
            loadProducts({ silent: true });
            loadCounts();
            return;
          }
        }
        const primaryImage = resolveImageRole(watchForm.primaryImage, pendingSnapshot, added);
        if (primaryImage && savedId) {
          try {
            saved = await updateFowebPortalWatch(savedId, { ...baseBody, primaryImage });
          } catch (_roleErr) {
            rolesFailed = true;
          }
        }
      } else {
        if (pendingSnapshot.length) {
          try {
            const uploadResult = await uploadFowebPortalWatchMedia(editing, pendingSnapshot);
            added = Array.isArray(uploadResult?.added) ? uploadResult.added : [];
            mediaUploaded = true;
            if (uploadResult) saved = uploadResult;
          } catch (uploadErr) {
            const message = uploadErr?.message || t.mediaUploadFailed;
            setFormError(message);
            toast.error(message);
            return;
          }
        }
        const primaryImage = resolveImageRole(watchForm.primaryImage, pendingSnapshot, added);
        try {
          saved = await updateFowebPortalWatch(editing, { ...baseBody, primaryImage });
        } catch (updateErr) {
          const message = updateErr?.message || t.saveFailed;
          if (mediaUploaded) {
            setFormError(`${t.mediaRolesFailed} (${message})`);
            toast.error(t.mediaRolesFailed);
            loadProducts({ silent: true });
            return;
          }
          throw updateErr;
        }
      }

      closeForm();
      if (rolesFailed) toast.error(t.mediaRolesFailed);
      else toast.success(wasNew ? t.toastCreated : t.toastUpdated);
      loadProducts({ silent: true });
      loadCounts();
    } catch (err) {
      const message = err?.message || t.saveFailed;
      setFormError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(item) {
    if (!window.confirm(t.deleteConfirm)) return;
    try {
      if (item.source === "watch") {
        await deleteFowebPortalWatch(item._id);
      } else {
        await deleteFowebPortalProduct(item._id);
      }
      await loadProducts();
      await loadCounts();
      toast.success(t.toastDeleted);
    } catch (err) {
      const message = err?.message || t.deleteFailed;
      setError(message);
      toast.error(message);
    }
  }

  async function onRemoveMedia(index) {
    if (!editingItem?._id) return;
    if (!window.confirm(t.mediaRemoveConfirm)) return;
    try {
      setFormError("");
      const removedPath = Array.isArray(editingItem.media) ? editingItem.media[index] : "";
      const updated =
        editingItem.source === "watch"
          ? watchRow(await deleteFowebPortalWatchMedia(editingItem._id, index))
          : await deleteFowebPortalProductMedia(editingItem._id, index);
      if (editingItem.source === "watch") {
        setWatchForm((current) => ({
          ...current,
          primaryImage:
            removedPath && current.primaryImage === removedPath ? "" : current.primaryImage,
        }));
      } else {
        setForm((current) => ({
          ...current,
          primaryImage:
            removedPath && current.primaryImage === removedPath ? "" : current.primaryImage,
          secondaryImage:
            removedPath && current.secondaryImage === removedPath ? "" : current.secondaryImage,
        }));
      }
      setItems((current) =>
        current.map((row) => (String(row._id) === String(updated._id) ? updated : row))
      );
    } catch (err) {
      setFormError(err?.message || t.saveFailed);
    }
  }

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i += 1) pageNumbers.push(i);

  return (
    <div className="portal-home">
      <header className="portal-home__header">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-3">
          <h1 className="text-lg font-semibold tracking-tight">{t.productManagement}</h1>
          <div className="flex items-center gap-4 text-sm">
            <LanguageToggle variant="gold" />
            <span className="hidden items-center gap-2 text-white/90 sm:inline-flex">
              <IconUser />
              {hello}
            </span>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-white/90 hover:text-white"
              onClick={onLogout}
            >
              <IconLogout />
              {t.logout}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-6">
        <PortalStatsCards
          createdRows={countUser?.createdObjectCount}
          approvedRows={countUser?.approvedObjectCount}
          rejectedCount={items.filter((item) => statusOf(item) === "rejected").length}
          onRejectedClick={() =>
            setStatusFilter((current) => (current === "rejected" ? "" : "rejected"))
          }
          lang={lang}
          t={t}
        />
        <section className="rounded-2xl bg-white p-6 shadow-sm">
          <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900">{t.submittedList}</h2>
            <button
              type="button"
              className="portal-home__add inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold"
              onClick={openTypeChoice}
            >
              <IconPlus />
              {t.addProduct}
            </button>
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-3">
            <label className="relative min-w-[14rem] flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
                <IconSearch />
              </span>
              <input
                className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t.searchPlaceholder}
              />
            </label>
            <label className="flex shrink-0 items-center gap-2 whitespace-nowrap text-xs text-slate-500">
              {t.colStatus}
              <select
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">{t.all}</option>
                <option value="created">{t.statusCreated}</option>
                <option value="review">{t.statusReview}</option>
                <option value="rejected">{t.statusRejected}</option>
              </select>
            </label>
            <button type="button" className="shrink-0 text-sm font-medium text-[#b89355]" onClick={clearFilters}>
              {t.clear}
            </button>
          </div>

          {error ? <p className="mb-3 text-sm text-rose-600">{error}</p> : null}

          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-xs font-medium uppercase tracking-wide text-slate-400">
                  <th className="whitespace-nowrap px-3 py-3">{t.colIndex}</th>
                  <th className="whitespace-nowrap px-3 py-3">{t.colImage}</th>
                  <th className="whitespace-nowrap px-3 py-3">{t.colName}</th>
                  <th className="whitespace-nowrap px-3 py-3">{t.colBrand}</th>
                  <th className="whitespace-nowrap px-3 py-3">{t.colPrice}</th>
                  <th className="whitespace-nowrap px-3 py-3">{t.colStatus}</th>
                  <th className="whitespace-nowrap px-3 py-3">{t.colDate}</th>
                  <th className="whitespace-nowrap px-3 py-3">{t.colActions}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td className="px-3 py-8 text-slate-400" colSpan={8}>
                      …
                    </td>
                  </tr>
                ) : pageItems.length === 0 ? (
                  <tr>
                    <td className="px-3 py-8 text-slate-400" colSpan={8}>
                      {t.empty}
                    </td>
                  </tr>
                ) : (
                  pageItems.map((item, index) => {
                    const src = mediaSrc(firstImage(item.media));
                    const added = formatSgDateParts(item.createdAt);
                    return (
                      <tr key={String(item._id)} className="border-b border-slate-100">
                        <td className="px-3 py-3 text-slate-500">
                          {(currentPage - 1) * PAGE_SIZE + index + 1}
                        </td>
                        <td className="px-3 py-3">
                          {src ? (
                            <img
                              src={src}
                              alt=""
                              className="h-12 w-12 rounded-md object-cover"
                            />
                          ) : (
                            <span className="block h-12 w-12 rounded-md bg-slate-100" />
                          )}
                        </td>
                        <td className="px-3 py-3 font-medium text-slate-800">{productTitle(item, lang)}</td>
                        <td className="px-3 py-3 text-slate-600">{brandLabel(item.brand)}</td>
                        <td className="px-3 py-3 text-slate-700">{formatRmb(item.sellingCostRMB)}</td>
                        <td className="px-3 py-3">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                              statusOf(item) === "rejected"
                                ? "bg-rose-50 text-rose-700"
                                : statusOf(item) === "review"
                                  ? "bg-amber-50 text-amber-800"
                                  : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {statusLabel(statusOf(item), t)}
                          </span>
                          {statusOf(item) === "rejected" && item.statusObject?.remarks ? (
                            <p className="mt-1 max-w-[14rem] text-xs font-normal text-rose-600">
                              {item.statusObject.remarks}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-3 py-3 text-xs leading-5 text-slate-500">
                          <div>{added.date}</div>
                          <div>{added.time}</div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-slate-200 px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50"
                              onClick={() => openEdit(item)}
                            >
                              <IconEdit />
                              {t.edit}
                            </button>
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-rose-200 px-2.5 py-1 text-xs text-rose-600 hover:bg-rose-50"
                              onClick={() => onDelete(item)}
                            >
                              <IconTrash />
                              {t.delete}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
            <p className="text-xs text-slate-500">{showingLabel(lang, from, to, filtered.length)}</p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="rounded-md border border-slate-200 px-2 py-1 text-sm text-slate-500 disabled:opacity-40"
                disabled={currentPage <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                ‹
              </button>
              {pageNumbers.map((num) => (
                <button
                  key={num}
                  type="button"
                  className={`min-w-[2rem] rounded-md border px-2 py-1 text-sm ${
                    num === currentPage
                      ? "portal-home__page--current"
                      : "border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                  onClick={() => setPage(num)}
                >
                  {num}
                </button>
              ))}
              <button
                type="button"
                className="rounded-md border border-slate-200 px-2 py-1 text-sm text-slate-500 disabled:opacity-40"
                disabled={currentPage >= totalPages}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              >
                ›
              </button>
            </div>
          </div>
        </section>
      </main>

      {choosingType ? (
        <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">{t.addProduct}</h3>
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                type="button"
                className="rounded-xl border border-slate-200 px-4 py-6 text-sm font-semibold text-slate-800 hover:border-[#c9a46a] hover:bg-[#f8f1e4]"
                onClick={chooseWatch}
              >
                {t.chooseWatch}
              </button>
              <button
                type="button"
                className="rounded-xl border border-slate-200 px-4 py-6 text-sm font-semibold text-slate-800 hover:border-[#c9a46a] hover:bg-[#f8f1e4]"
                onClick={openCreate}
              >
                {t.chooseOther}
              </button>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
                onClick={closeTypeChoice}
              >
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editing && formKind !== "watch" ? (
        <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40 p-4">
          <form
            className="max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl"
            onSubmit={onSave}
          >
            <h3 className="mb-4 text-lg font-semibold">
              {editing === "new" ? t.addProduct : t.editProduct}
            </h3>
            {formError ? <p className="mb-3 text-sm text-rose-600">{formError}</p> : null}
            {editingStatus === "rejected" ? (
              <div className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
                <p>{t.rejectedBanner}</p>
                {editingItem?.statusObject?.remarks ? (
                  <p className="mt-1">
                    {t.lastRejectReason}: {editingItem.statusObject.remarks}
                  </p>
                ) : null}
              </div>
            ) : null}
            {editingStatus === "review" ? (
              <div className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <p>{t.reviewBanner}</p>
                {editingItem?.statusObject?.remarks ? (
                  <p className="mt-1">
                    {t.lastRejectReason}: {editingItem.statusObject.remarks}
                  </p>
                ) : null}
              </div>
            ) : null}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-12">
              <label className="text-sm font-medium text-slate-700 sm:col-span-3">
                {t.brand} *
                <BrandSearchSelect
                  brands={brands}
                  value={form.brand}
                  onChange={(brand) => applyProductFields({ brand })}
                  placeholder={t.selectBrand}
                  emptyText={t.noMatchingBrand}
                  disabled={saving}
                />
              </label>
              <label className="text-sm font-medium text-slate-700 sm:col-span-3">
                {t.category} *
                <select
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal"
                  value={form.category}
                  onChange={(e) => onCategoryChange(e.target.value)}
                  required
                >
                  <option value="">{t.selectCategory}</option>
                  {categories.map((row) => (
                    <option key={String(row._id)} value={row._id}>
                      {catalogLabel(row, lang)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-medium text-slate-700 sm:col-span-3">
                {t.subcategory} {subcategoryOptions.length ? "*" : ""}
                <select
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal"
                  value={form.subcategory}
                  onChange={(e) => onSubcategoryChange(e.target.value)}
                  required={subcategoryOptions.length > 0}
                  disabled={!form.category}
                >
                  <option value="">
                    {!form.category
                      ? t.selectCategoryFirst
                      : subcategoryOptions.length
                        ? t.selectSubcategory
                        : t.noSubcategories}
                  </option>
                  {subcategoryOptions.map((sub) => (
                    <option key={sub.name} value={sub.name}>
                      {catalogLabel(sub, lang)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-medium text-slate-700 sm:col-span-3">
                {t.subsubcategory} {subsubcategoryOptions.length ? "*" : ""}
                <select
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal"
                  value={form.subsubcategory}
                  onChange={(e) => applyProductFields({ subsubcategory: e.target.value })}
                  required={subsubcategoryOptions.length > 0}
                  disabled={!form.subcategory}
                >
                  <option value="">
                    {!form.subcategory
                      ? t.selectSubcategoryFirst
                      : subsubcategoryOptions.length
                        ? t.selectSubsubcategory
                        : t.noSubsubcategories}
                  </option>
                  {subsubcategoryOptions.map((node) => (
                    <option key={node.name} value={node.name}>
                      {catalogLabel(node, lang)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-medium text-slate-700 sm:col-span-6">
                {t.enProductName} *
                <input
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal"
                  value={form.name}
                  onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
                />
              </label>
              <label className="text-sm font-medium text-slate-700 sm:col-span-6">
                {t.cnName} *
                <input
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal"
                  value={form.cnName}
                  onChange={(e) => setForm((current) => ({ ...current, cnName: e.target.value }))}
                />
              </label>
              <div className="sm:col-span-12">
                <p className="text-sm font-medium text-slate-700">{t.genderType} *</p>
                <div
                  className="mt-1 grid grid-cols-3 gap-2 sm:grid-cols-6"
                  role="group"
                  aria-label={t.genderType}
                >
                  {GENDER_TYPES.map((value) => {
                    const selected = form.genderType === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() =>
                          setForm((current) => ({ ...current, genderType: value }))
                        }
                        className={`rounded-lg border px-2 py-2 text-sm font-medium leading-snug transition-colors ${
                          selected
                            ? "border-[#c9a46a] bg-[#f8f1e4] text-slate-800"
                            : "border-slate-200 bg-white text-slate-700 hover:border-[#c9a46a]"
                        }`}
                      >
                        {t[`gender${value}`] || value}
                      </button>
                    );
                  })}
                </div>
              </div>
              <label className="text-sm font-medium text-slate-700 sm:col-span-6">
                {t.costRmb}
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal"
                  value={form.sellingCostRMB}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, sellingCostRMB: e.target.value }))
                  }
                />
              </label>
              <label className="text-sm font-medium text-slate-700 sm:col-span-6">
                {t.supplier}
                <input
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal"
                  value={form.supplier}
                  onChange={(e) => setForm((current) => ({ ...current, supplier: e.target.value }))}
                />
              </label>
              <label className="text-sm font-medium text-slate-700 sm:col-span-6">
                {t.enDescription}
                <textarea
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal"
                  value={form.enDescription}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, enDescription: e.target.value }))
                  }
                />
              </label>
              <label className="text-sm font-medium text-slate-700 sm:col-span-6">
                {t.cnDescription}
                <textarea
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal"
                  value={form.cnDescription}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, cnDescription: e.target.value }))
                  }
                />
              </label>
              <div className="flex min-h-0 flex-col sm:col-span-6">
                <PortalMediaUpload
                  label={t.media}
                  dropText={t.mediaDrop}
                  hint={t.mediaHint}
                  roleHint={t.mediaRoleHint}
                  primaryLabel={t.mediaPrimary}
                  secondaryLabel={t.mediaSecondary}
                  existingMedia={editingItem?.media || []}
                  pendingFiles={files}
                  onPendingFilesChange={setFiles}
                  onRemoveExisting={onRemoveMedia}
                  disabled={saving}
                  primaryImage={form.primaryImage}
                  secondaryImage={form.secondaryImage}
                  onPrimaryImageChange={(value) =>
                    setForm((current) => ({ ...current, primaryImage: value }))
                  }
                  onSecondaryImageChange={(value) =>
                    setForm((current) => ({ ...current, secondaryImage: value }))
                  }
                />
              </div>
              <label className="flex min-h-0 flex-col text-sm font-medium text-slate-700 sm:col-span-6">
                {t.remarks}
                <textarea
                  rows={6}
                  className="mt-1 h-40 min-h-[10rem] w-full flex-1 resize-y rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal"
                  value={form.remarks}
                  onChange={(e) => setForm((current) => ({ ...current, remarks: e.target.value }))}
                />
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600"
                onClick={closeForm}
                disabled={saving}
              >
                {t.cancel}
              </button>
              <button
                type="submit"
                className="portal-home__add rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-60"
                disabled={saving}
              >
                {saving
                  ? editing === "new"
                    ? t.creating
                    : t.saving
                  : editing === "new"
                    ? t.create
                    : t.save}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {editing && formKind === "watch" ? (
        <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40 p-4">
          <form
            className="max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl"
            onSubmit={onSaveWatch}
          >
            <h3 className="mb-4 text-lg font-semibold">
              {editing === "new" ? t.addWatch : t.editWatch}
            </h3>
            {formError ? <p className="mb-3 text-sm text-rose-600">{formError}</p> : null}
            {editingStatus === "rejected" ? (
              <div className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
                <p>{t.rejectedBanner}</p>
                {editingItem?.statusObject?.remarks ? (
                  <p className="mt-1">
                    {t.lastRejectReason}: {editingItem.statusObject.remarks}
                  </p>
                ) : null}
              </div>
            ) : null}
            {editingStatus === "review" ? (
              <div className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <p>{t.reviewBanner}</p>
              </div>
            ) : null}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-12">
              <label className="text-sm font-medium text-slate-700 sm:col-span-4">
                {t.watchName}
                <input
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal"
                  value={watchForm.watchName}
                  onChange={(e) =>
                    setWatchForm((current) => ({ ...current, watchName: e.target.value }))
                  }
                />
              </label>
              <label className="text-sm font-medium text-slate-700 sm:col-span-4">
                {t.cnName}
                <input
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal"
                  value={watchForm.cnName}
                  onChange={(e) =>
                    setWatchForm((current) => ({ ...current, cnName: e.target.value }))
                  }
                />
              </label>
              <label className="text-sm font-medium text-slate-700 sm:col-span-4">
                {t.brand} *
                <select
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal"
                  value={watchForm.category}
                  onChange={(e) => {
                    const category = e.target.value;
                    setWatchForm((current) => ({
                      ...current,
                      category,
                      specification: { ...current.specification, brand: category },
                    }));
                  }}
                  required
                >
                  <option value="">{t.selectBrand}</option>
                  {WATCH_BRANDS.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-medium text-slate-700 sm:col-span-6">
                {t.costRmb}
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal"
                  value={watchForm.sellingCostRMB}
                  onChange={(e) =>
                    setWatchForm((current) => ({ ...current, sellingCostRMB: e.target.value }))
                  }
                />
              </label>
              <label className="text-sm font-medium text-slate-700 sm:col-span-6">
                {t.supplier}
                <input
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal"
                  value={watchForm.supplier}
                  onChange={(e) =>
                    setWatchForm((current) => ({ ...current, supplier: e.target.value }))
                  }
                />
              </label>
              <label className="text-sm font-medium text-slate-700 sm:col-span-6">
                {t.enDescription}
                <textarea
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal"
                  value={watchForm.description}
                  onChange={(e) =>
                    setWatchForm((current) => ({ ...current, description: e.target.value }))
                  }
                />
              </label>
              <label className="text-sm font-medium text-slate-700 sm:col-span-6">
                {t.cnDescription}
                <textarea
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal"
                  value={watchForm.cnDescription}
                  onChange={(e) =>
                    setWatchForm((current) => ({ ...current, cnDescription: e.target.value }))
                  }
                />
              </label>
              <div className="sm:col-span-12">
                <p className="text-sm font-medium text-slate-700">{t.specification}</p>
                <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-12">
                  {PORTAL_FORM_SPECIFICATION_FIELDS.map((key) => (
                    <label key={key} className="text-sm font-medium text-slate-700 sm:col-span-4">
                      {t.spec[key]}
                      <input
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal"
                        value={watchForm.specification?.[key] || ""}
                        onChange={(e) =>
                          setWatchForm((current) => ({
                            ...current,
                            specification: { ...current.specification, [key]: e.target.value },
                          }))
                        }
                      />
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex min-h-0 flex-col sm:col-span-12">
                <PortalMediaUpload
                  label={t.media}
                  dropText={t.mediaDrop}
                  hint={t.mediaHint}
                  roleHint={t.mediaPrimaryOnlyHint}
                  primaryLabel={t.mediaPrimary}
                  existingMedia={editingItem?.media || []}
                  pendingFiles={files}
                  onPendingFilesChange={setFiles}
                  onRemoveExisting={onRemoveMedia}
                  disabled={saving}
                  primaryOnly
                  primaryImage={watchForm.primaryImage}
                  onPrimaryImageChange={(value) =>
                    setWatchForm((current) => ({ ...current, primaryImage: value }))
                  }
                />
              </div>
              <label className="flex min-h-0 flex-col text-sm font-medium text-slate-700 sm:col-span-12">
                {t.remarks}
                <textarea
                  rows={6}
                  className="mt-1 h-40 min-h-[10rem] w-full flex-1 resize-y rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal"
                  value={watchForm.remarks}
                  onChange={(e) =>
                    setWatchForm((current) => ({ ...current, remarks: e.target.value }))
                  }
                />
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600"
                onClick={closeForm}
                disabled={saving}
              >
                {t.cancel}
              </button>
              <button
                type="submit"
                className="portal-home__add rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-60"
                disabled={saving}
              >
                {saving
                  ? editing === "new"
                    ? t.creating
                    : t.saving
                  : editing === "new"
                    ? t.create
                    : t.save}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

export default HomePage;
